import { Fragment, useCallback, useId, useState } from 'react'

import {
  type Chosen,
  type SourceEntry,
  browse,
  chooseFolder,
  chosenFolders,
  unchooseFolder,
} from '../../api/connections'
import { asFailure, type Failure } from '../../api/request'
import { useLoad } from '../../api/useLoad'
import { useAnnounce } from '../../ui/Announce'
import { Button } from '../../ui/Control'
import { Icon } from '../../ui/Icon'
import { Failed, Waiting } from '../../ui/Status'
import { asDate, asSize } from '../library/Tiles'
import { arranged, coveringOf, isChosen, nameOf, ROOT, spoken, stepsTo, tidy } from './paths'

/**
 * Choosing what comes over from a foreign system: one level at a time, folders ticked, files shown.
 *
 * **One column with a way back, on every screen.** The plan called this a tree, and a tree with
 * disclosure arrows is what a desktop would draw — but it would be a second rendering of the same
 * choice, needed on a phone anyway, and the two would drift apart. A level plus a breadcrumb is what
 * a phone can carry, is operable with nothing but Tab and Space, and matches what the connection can
 * actually do: **one request per folder opened**, because there is no subtree call over there and
 * there cannot be one. An indented tree would invite somebody to open six levels at once and pay for
 * it in six seconds of somebody else's server.
 *
 * Three things are said here rather than left to be worked out. **Files cannot be ticked**, and the
 * line above the list says why instead of a control standing there greyed out: a file chosen by hand
 * could not bring the next one with it. **A folder inside a ticked one is covered**, not a second
 * choice — the core allows ticking it and would make nothing of it, so offering a tick would be
 * offering a control that does nothing. And **unticking says what stays**, at the control, because
 * that is what makes it answerable without thinking twice.
 *
 * What is ticked is a read of MyLib's own database and is therefore always on the screen; a *level*
 * costs a call to the other system, so it is fetched only once somebody asks to walk.
 */
export function Folders({
  connectionId,
  address,
  usable,
}: {
  readonly connectionId: string
  readonly address: string
  /**
   * Whether the password behind this connection can still be read.
   *
   * Walking needs it, so an unusable connection is not offered a walk — but what is already ticked
   * is still shown and can still be taken back, because none of that touches the other system.
   */
  readonly usable: boolean
}) {
  const announce = useAnnounce()
  const chosen = useLoad(
    useCallback((signal: AbortSignal) => chosenFolders(connectionId, signal), [connectionId]),
  )
  const [walking, setWalking] = useState(false)
  const [working, setWorking] = useState<string | null>(null)
  const [problem, setProblem] = useState<Failure | null>(null)

  const list = chosen.load.status === 'ready' ? chosen.load.value : []

  const untick = async (path: string) => {
    setProblem(null)
    setWorking(path)
    try {
      await unchooseFolder(connectionId, path)
      chosen.retry()
      announce('Unticked. Everything already taken over stays in your library.')
    } catch (error) {
      setProblem(asFailure(error))
    } finally {
      setWorking(null)
    }
  }

  return (
    <section className="folders" aria-label={`Folders taken over from ${address}`}>
      <h4 className="folders__title">Folders taken over</h4>

      {chosen.load.status === 'waiting' && <Waiting label="Looking up what you have ticked." />}
      {chosen.load.status === 'failed' && (
        <Failed failure={chosen.load.failure} onRetry={chosen.retry} />
      )}
      {problem !== null && <Failed failure={problem} />}

      {chosen.load.status === 'ready' &&
        (list.length === 0 ? (
          <p className="folders__none">
            Nothing is ticked yet, so nothing comes over from here. Walk the folders below and tick
            the ones you want — everything inside a ticked folder arrives, including whatever
            appears in it later.
          </p>
        ) : (
          <>
            <ul className="chosen">
              {list.map((one) => (
                <li className="chosen__one" key={one.path}>
                  <span className="chosen__path">
                    {one.path === ROOT ? 'Everything you can see' : one.path}
                  </span>
                  <Button
                    tone="bare"
                    aria-label={`Untick ${spoken(one.path)}`}
                    disabled={working === one.path}
                    onClick={() => {
                      void untick(one.path)
                    }}
                  >
                    Untick
                  </Button>
                </li>
              ))}
            </ul>
            {/*
              At the controls and not behind them. Unticking is the one thing here somebody might
              hesitate over, and the reason not to hesitate is that it takes nothing away.
            */}
            <p className="folders__note">
              Unticking stops the syncing for that folder and does nothing else. Everything already
              taken over stays in your library — openable, downloadable and findable exactly as it
              is now.
            </p>
          </>
        ))}

      {usable && (
        <div className="controls controls--tight">
          <Button
            tone="quiet"
            aria-label={
              walking ? `Stop choosing folders from ${address}` : `Choose folders from ${address}`
            }
            onClick={() => {
              setWalking(!walking)
            }}
          >
            {walking ? 'Done choosing' : 'Choose folders'}
          </Button>
        </div>
      )}

      {usable && walking && (
        <Walk connectionId={connectionId} chosen={list} onChanged={chosen.retry} />
      )}
    </section>
  )
}

/**
 * The walk itself: where you are, what lies there, and a field for a path you already know.
 *
 * It holds its own place in the tree rather than taking one from the address, and that is on purpose:
 * this is a foreign system's tree inside somebody's account settings, not a place in MyLib. A link to
 * it would be a link to somebody else's folder, and there is nothing on the other side to send it to.
 */
function Walk({
  connectionId,
  chosen,
  onChanged,
}: {
  readonly connectionId: string
  readonly chosen: readonly Chosen[]
  /** Asks the surrounding list for what is ticked again, once this has changed it. */
  readonly onChanged: () => void
}) {
  const announce = useAnnounce()
  const base = useId()
  const [here, setHere] = useState(ROOT)
  const level = useLoad(
    useCallback((signal: AbortSignal) => browse(connectionId, here, signal), [connectionId, here]),
  )
  const [working, setWorking] = useState<string | null>(null)
  /** A refusal about one entry, kept with the path so it is drawn at that row and not at the top. */
  const [problem, setProblem] = useState<{ readonly at: string; readonly failure: Failure } | null>(
    null,
  )
  const [typed, setTyped] = useState('')
  const [typedProblem, setTypedProblem] = useState<Failure | null>(null)

  /** Ticks or unticks one path, and puts whatever the service says about it where that path is. */
  const decide = async (path: string, tick: boolean) => {
    setProblem(null)
    setWorking(path)
    try {
      if (tick) {
        await chooseFolder(connectionId, path)
      } else {
        await unchooseFolder(connectionId, path)
      }
      onChanged()
      announce(
        tick
          ? 'Ticked. Everything in it comes over, now and whenever something is added to it.'
          : 'Unticked. Everything already taken over stays in your library.',
      )
    } catch (error) {
      setProblem({ at: path, failure: asFailure(error) })
    } finally {
      setWorking(null)
    }
  }

  const wanted = tidy(typed)

  /**
   * Ticks a path somebody typed — through the same call as a tick on a row, and deliberately so.
   *
   * The service reads the path over there before it keeps it, so a slip comes back at once as "there
   * is nothing at that path" instead of sitting in the list looking like a choice. What worked is
   * then walked to, because seeing the folder is how somebody knows they typed the one they meant.
   */
  const takeTyped = async () => {
    setTypedProblem(null)
    setWorking(wanted)
    try {
      await chooseFolder(connectionId, wanted)
      onChanged()
      setTyped('')
      setHere(wanted)
      announce('Ticked. Everything in it comes over, now and whenever something is added to it.')
    } catch (error) {
      setTypedProblem(asFailure(error))
    } finally {
      setWorking(null)
    }
  }

  const entries = level.load.status === 'ready' ? arranged(level.load.value.entries) : []

  return (
    <div className="walk">
      {/*
        Where you are, and the way back up. The same shape as the library's trail, in the library's
        own classes: it is the same question — "where am I, and how do I get out" — and two readings
        of it would be two things to learn.
      */}
      <nav className="trail" aria-label="Where you are in that system">
        {here === ROOT ? (
          <span className="trail__here">Everything you can see</span>
        ) : (
          <button
            type="button"
            className="trail__step"
            onClick={() => {
              setHere(ROOT)
            }}
          >
            Everything you can see
          </button>
        )}
        {stepsTo(here).map((step, index, all) => (
          <Fragment key={step}>
            <span className="trail__slash" aria-hidden="true">
              /
            </span>
            {index === all.length - 1 ? (
              <span className="trail__here">{nameOf(step)}</span>
            ) : (
              <button
                type="button"
                className="trail__step"
                onClick={() => {
                  setHere(step)
                }}
              >
                {nameOf(step)}
              </button>
            )}
          </Fragment>
        ))}
      </nav>

      {/*
        Why there is no tick beside a file. One sentence above the list, rather than a control per
        file that stands there doing nothing: a dead control is a question, and this is the answer.
      */}
      <p className="walk__why">
        You tick folders and never single files, so that whatever is added over there later comes
        over too. Files are shown so you can see what a folder holds.
      </p>

      {level.load.status === 'waiting' && <Waiting label="Reading that folder." />}
      {level.load.status === 'failed' && (
        <Failed
          failure={level.load.failure}
          onRetry={level.retry}
          title="That folder could not be read"
        />
      )}

      {level.load.status === 'ready' &&
        (entries.length === 0 ? (
          <p className="walk__empty">There is nothing in this folder.</p>
        ) : (
          <ul className="entries">
            {entries.map((entry) => (
              <Entry
                key={entry.path}
                entry={entry}
                ticked={isChosen(chosen, entry.path)}
                covering={coveringOf(chosen, entry.path)}
                busy={working === entry.path}
                failure={problem !== null && problem.at === entry.path ? problem.failure : null}
                onGoInto={() => {
                  setHere(entry.path)
                }}
                onDecide={(tick) => {
                  void decide(entry.path, tick)
                }}
              />
            ))}
          </ul>
        ))}

      {/*
        A path somebody already knows, typed instead of walked to. The tree over there is the whole
        magazine of a university — fifty branches at the top — so somebody who knows where their
        course lies should not have to click down to it every semester.
      */}
      <div className="field">
        <label className="field__label" htmlFor={`${base}-path`}>
          A path you already know
        </label>
        <input
          id={`${base}-path`}
          className="field__control"
          spellCheck={false}
          autoComplete="off"
          value={typed}
          aria-invalid={typedProblem !== null}
          aria-describedby={
            typedProblem === null ? `${base}-path-hint` : `${base}-path-hint ${base}-path-problem`
          }
          onChange={(event) => {
            setTyped(event.target.value)
            setTypedProblem(null)
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && wanted !== '') {
              event.preventDefault()
              void takeTyped()
            }
          }}
        />
        <p className="field__hint" id={`${base}-path-hint`}>
          The folders as that system names them, one after another, separated by slashes — for
          example FHOED/Rechtswissenschaft/Staatsrecht.
        </p>
        {typedProblem !== null && (
          <p className="field__problem" id={`${base}-path-problem`} role="alert">
            {typedProblem.message}
          </p>
        )}
        <div className="controls controls--tight">
          <Button
            tone="quiet"
            disabled={wanted === '' || working === wanted}
            onClick={() => {
              void takeTyped()
            }}
          >
            Tick that folder
          </Button>
        </div>
      </div>
    </div>
  )
}

/**
 * One thing in the level: a folder to tick and walk into, or a file that is simply there.
 *
 * The box and the name are siblings rather than one inside the other. A checkbox inside the control
 * that walks into the folder would mean every tick walks somewhere, and every walk toggles a tick.
 */
function Entry({
  entry,
  ticked,
  covering,
  busy,
  failure,
  onGoInto,
  onDecide,
}: {
  readonly entry: SourceEntry
  readonly ticked: boolean
  /** The already ticked folder this one lies inside, or `null`. See {@link coveringOf}. */
  readonly covering: Chosen | null
  readonly busy: boolean
  readonly failure: Failure | null
  readonly onGoInto: () => void
  readonly onDecide: (tick: boolean) => void
}) {
  const covered = covering !== null

  return (
    <li className={entry.folder ? 'entry' : 'entry entry--file'}>
      {/*
        A real checkbox, as in the task list: announced as "checked" without anybody being told,
        worked with the space bar, and made finger-sized by the stylesheet rather than by a button
        drawn to look like one. A file has none, and a folder that is already covered has none —
        both keep the column, so the names below stay in one line and the absence is visible.
      */}
      {entry.folder && !covered && (
        <input
          type="checkbox"
          className="entry__tick"
          checked={ticked}
          disabled={busy}
          aria-label={ticked ? `Untick ${entry.name}` : `Tick ${entry.name}`}
          onChange={(event) => {
            onDecide(event.target.checked)
          }}
        />
      )}
      {(!entry.folder || covered) && <span className="entry__blank" aria-hidden="true" />}

      {entry.folder ? (
        <button type="button" className="entry__open" onClick={onGoInto}>
          <span className="entry__name">{entry.name}</span>
          <Icon name="details" small />
        </button>
      ) : (
        <span className="entry__name entry__name--file">{entry.name}</span>
      )}

      <span className="entry__facts">
        {!entry.folder && entry.sizeInBytes !== null && <span>{asSize(entry.sizeInBytes)}</span>}
        {entry.changedAt !== null && <span>{asDate(entry.changedAt)}</span>}
      </span>

      {covering !== null && (
        <p className="entry__covered">
          {`Comes over already — it lies inside ${spoken(covering.path)}.`}
        </p>
      )}

      {failure !== null && <Failed failure={failure} />}
    </li>
  )
}
